import React, { useState } from "react";
import { App, Flex } from "antd";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { useImportOrganizationsFromCsvMutation } from "@/redux/service";
import { SimpleModalProps } from "@/components_v2/Modal/types";
import { TFunction } from "i18next/typescript/t";
import BaseModal from "@/components_v2/Modal/BaseModal";
import BaseButton from "@/components_v2/Button/BaseButton";
import FormFieldWrapper from "@/components_v2/Wrapper/FormFieldWrapper";
import {
  HookFormBaseUpload,
  HookFormInput,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import {
  OrganizationImportResponseError,
  OrganizationImportRequest,
} from "@/redux/organizations/types";
import { UploadFile } from "antd/es/upload/interface";
import { ImportOrganizationsFormValues } from "@/features/organizations/list/types";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";

const Errors = ({ errors }: { errors: OrganizationImportResponseError[] }) => {
  return (
    <div>
      {errors.map((error) => (
        <div key={error.rowIndex}>
          <ul>
            <li>Row Index - {error.rowIndex}</li>
            <li>Row Data - {error.row.join(", ")}</li>
            <li>
              Row Errors -{" "}
              {Array.isArray(error.errors)
                ? error.errors.join(", ")
                : JSON.stringify(error.errors)}
            </li>
          </ul>
          <hr />
        </div>
      ))}
    </div>
  );
};

const importOrganizationsSchema = (t: TFunction) =>
  yup.object().shape({
    file: yup
      .mixed<UploadFile>()
      .required(t("form.validation.required"))
      .test(
        "fileType",
        t("form.validation.allowedFileExtensions", { extensions: "CSV" }),
        (value) => {
          if (!(value instanceof File)) return true;
          const lastDot = value.name.lastIndexOf(".");
          const extension = lastDot === -1 ? "" : value.name.substring(lastDot);

          return [".csv"].includes(extension);
        },
      ),
    adminEmailIndex: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .typeError(t("form.validation.required"))
      .required(t("form.validation.required")),
    nameIndex: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .typeError(t("form.validation.notValid"))
      .required(t("form.validation.required")),
    addressIndex: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .typeError(t("form.validation.notValid"))
      .required(t("form.validation.required")),
    externalIdIndex: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .typeError(t("form.validation.notValid"))
      .required(t("form.validation.required")),
    contactEmailIndex: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .typeError(t("form.validation.notValid"))
      .required(t("form.validation.required")),
    countryIndex: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .typeError(t("form.validation.notValid"))
      .nullable()
      .default(null),
    websiteIndex: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .typeError(t("form.validation.notValid"))
      .nullable()
      .default(null),
    phoneNumberIndex: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .typeError(t("form.validation.notValid"))
      .nullable()
      .default(null),
  });

const ImportOrganizationsModal = ({ open, setOpen }: SimpleModalProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const [importOrganizations] = useImportOrganizationsFromCsvMutation();
  const [errors, setErrors] = useState<OrganizationImportResponseError[]>([]);

  const form = useForm<ImportOrganizationsFormValues>({
    resolver: yupResolver(importOrganizationsSchema(t)),
    mode: "onChange",
    defaultValues: {
      file: undefined,
      adminEmailIndex: undefined,
      nameIndex: undefined,
      addressIndex: undefined,
      externalIdIndex: undefined,
      contactEmailIndex: undefined,
      phoneNumberIndex: undefined,
      websiteIndex: undefined,
      countryIndex: undefined,
    },
  });

  const closeModal = () => {
    setOpen(false);
    form.reset();
  };

  const handleSubmit = (data: ImportOrganizationsFormValues) => {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (![null, undefined].includes(value)) {
        formData.set(key, value);
      }
    });

    importOrganizations(formData as unknown as OrganizationImportRequest)
      .unwrap()
      .then((data) => {
        if (data.errors.length === 0) {
          closeModal();
        } else {
          setErrors(data.errors);
        }
        notification.success({
          message: t("importedOrganization", {
            orgCount: data.importedOrganizationsCount,
          }),
        });
      });
  };

  const fields = [
    {
      label: t("organizations.labels.adminEmailIndex"),
      required: true,
      field: (
        <HookFormInput
          name="adminEmailIndex"
          type="number"
          required
          placeholder={t("organizations.placeholders.enterAdminEmailIndex")}
          control={form.control}
        />
      ),
    },
    {
      label: t("organizations.labels.nameIndex"),
      required: true,
      field: (
        <HookFormInput
          name="nameIndex"
          type="number"
          required
          placeholder={t("organizations.placeholders.enterNameIndex")}
          control={form.control}
        />
      ),
    },
    {
      label: t("organizations.labels.addressIndex"),
      required: true,
      field: (
        <HookFormInput
          name="addressIndex"
          type="number"
          required
          placeholder={t("organizations.placeholders.enterAddressIndex")}
          control={form.control}
        />
      ),
    },
    {
      label: t("organizations.labels.externalIdIndex"),
      required: true,
      field: (
        <HookFormInput
          name="externalIdIndex"
          type="number"
          placeholder={t("organizations.placeholders.enterExternalIdIndex")}
          control={form.control}
        />
      ),
    },
    {
      label: t("organizations.labels.contactEmailIndex"),
      required: true,
      field: (
        <HookFormInput
          name="contactEmailIndex"
          type="number"
          placeholder={t("organizations.placeholders.enterContactEmailIndex")}
          control={form.control}
        />
      ),
    },
    {
      label: t("organizations.labels.countryIndex"),
      field: (
        <HookFormInput
          name="countryIndex"
          type="number"
          placeholder={t("organizations.placeholders.enterCountryIndex")}
          control={form.control}
        />
      ),
    },
    {
      label: t("organizations.labels.websiteIndex"),
      field: (
        <HookFormInput
          name="websiteIndex"
          type="number"
          placeholder={t("organizations.placeholders.enterWebsiteIndex")}
          control={form.control}
        />
      ),
    },
    {
      label: t("organizations.labels.phoneNumberIndex"),
      field: (
        <HookFormInput
          name="phoneNumberIndex"
          type="number"
          placeholder={t("organizations.placeholders.enterPhoneNumberIndex")}
          control={form.control}
        />
      ),
    },
    {
      label: t("organizations.labels.file"),
      required: true,
      field: (
        <HookFormBaseUpload
          name="file"
          control={form.control}
          supportedExtensions={["csv"]}
          helpText={t("organizations.labels.csvFileImportHelpText")}
        />
      ),
    },
  ];

  return (
    <BaseModal
      title={t("organizations.labels.importOrganizations")}
      subtitle={t("organizations.labels.importCsvOrganizationsHelpText")}
      open={open}
      onCancel={closeModal}
      footer={
        <Flex wrap justify="space-between" align="center" gap={"small"}>
          <BaseButton
            size={BUTTON_SIZES.SMALL}
            variant={BUTTON_VARIANTS.TERTIARY}
            onClick={() => setOpen(false)}
          >
            {t("buttons.cancel")}
          </BaseButton>

          <BaseButton
            variant="primary"
            size="small"
            disabled={form.formState.isSubmitting}
            onClick={form.handleSubmit(handleSubmit)}
          >
            {t("buttons.submit")}
          </BaseButton>
        </Flex>
      }
    >
      <div className="import-organizations-modal-form-fields">
        {fields.map((field) => (
          <FormFieldWrapper
            key={field.label}
            label={field.label}
            required={field.required}
          >
            {field.field}
          </FormFieldWrapper>
        ))}

        {errors && <Errors errors={errors} />}
      </div>
    </BaseModal>
  );
};

export default ImportOrganizationsModal;
